import type {
  CancelarParcelaInput,
  CreateParcelaInput,
  FinancialAuditLog,
  FinancialCharts,
  FinancialDashboard,
  ManagementDashboard,
  FinancialEvent,
  FinancialKpis,
  FinancialPlan,
  GeneratePlanInput,
  LancamentoInput,
  Parcela,
  UpdateParcelaInput,
} from '@internal/orquestra-contract';
import { OrquestraReportClient } from './OrquestraReportClient';
import type { AuditLogsPage, AuditLogsPageParams } from './OrquestraApi.types';

function financialBase(projectId: number): string {
  return `/projects/${projectId}/financial`;
}

/**
 * Estende `OrquestraClient` com os métodos do módulo Financeiro (Fase 3) e do
 * dashboard (3.1). Classe intermediária — `OrquestraProcessAsIsClient` a estende
 * e implementa o `OrquestraApi` completo.
 */
export class OrquestraFinancialClient extends OrquestraReportClient {
  async listParcelas(projectId: number): Promise<Parcela[]> {
    return this.request<Parcela[]>(`${financialBase(projectId)}/parcelas`);
  }

  async getParcela(projectId: number, parcelaId: number): Promise<Parcela> {
    return this.request<Parcela>(
      `${financialBase(projectId)}/parcelas/${parcelaId}`,
    );
  }

  async createParcela(
    projectId: number,
    input: CreateParcelaInput,
  ): Promise<Parcela> {
    return this.request<Parcela>(
      `${financialBase(projectId)}/parcelas`,
      this.jsonInit('POST', input),
    );
  }

  async updateParcela(
    projectId: number,
    parcelaId: number,
    input: UpdateParcelaInput & { reason?: string | null },
  ): Promise<Parcela> {
    return this.request<Parcela>(
      `${financialBase(projectId)}/parcelas/${parcelaId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteParcela(
    projectId: number,
    parcelaId: number,
    deletionReason?: string | null,
  ): Promise<void> {
    await this.request(
      `${financialBase(projectId)}/parcelas/${parcelaId}`,
      this.jsonInit('DELETE', { deletionReason }),
    );
  }

  async lancarParcela(
    projectId: number,
    parcelaId: number,
    input: LancamentoInput,
  ): Promise<Parcela> {
    return this.request<Parcela>(
      `${financialBase(projectId)}/parcelas/${parcelaId}/lancamento`,
      this.jsonInit('POST', input),
    );
  }

  async cancelarParcela(
    projectId: number,
    parcelaId: number,
    input: CancelarParcelaInput,
  ): Promise<Parcela> {
    return this.request<Parcela>(
      `${financialBase(projectId)}/parcelas/${parcelaId}/cancelar`,
      this.jsonInit('POST', input),
    );
  }

  async getFinancialKpis(projectId: number): Promise<FinancialKpis> {
    return this.request<FinancialKpis>(`${financialBase(projectId)}/kpis`);
  }

  async getPlan(projectId: number): Promise<FinancialPlan> {
    return this.request<FinancialPlan>(`${financialBase(projectId)}/plan`);
  }

  async generatePlan(
    projectId: number,
    input: GeneratePlanInput,
  ): Promise<FinancialPlan> {
    return this.request<FinancialPlan>(
      `${financialBase(projectId)}/plan`,
      this.jsonInit('POST', input),
    );
  }

  async updatePlan(
    projectId: number,
    input: GeneratePlanInput,
  ): Promise<FinancialPlan> {
    return this.request<FinancialPlan>(
      `${financialBase(projectId)}/plan`,
      this.jsonInit('PUT', input),
    );
  }

  async getFinancialEvents(projectId: number): Promise<FinancialEvent[]> {
    return this.request<FinancialEvent[]>(`${financialBase(projectId)}/events`);
  }

  async getFinancialCharts(projectId: number): Promise<FinancialCharts> {
    return this.request<FinancialCharts>(`${financialBase(projectId)}/charts`);
  }

  async getAuditLogs(projectId: number): Promise<FinancialAuditLog[]> {
    return this.request<FinancialAuditLog[]>(
      `${financialBase(projectId)}/audit-logs`,
    );
  }

  async getAuditLogsPage(
    projectId: number,
    params: AuditLogsPageParams = {},
  ): Promise<AuditLogsPage> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    if (params.offset !== undefined) search.set('offset', String(params.offset));
    if (params.actionTypes && params.actionTypes.length > 0) {
      search.set('actionTypes', params.actionTypes.join(','));
    }
    const qs = search.toString();
    return this.request<AuditLogsPage>(
      `${financialBase(projectId)}/audit-logs/page${qs ? `?${qs}` : ''}`,
    );
  }

  async getFinancialDashboard(): Promise<FinancialDashboard> {
    return this.request<FinancialDashboard>('/dashboards/financial');
  }

  async getManagementDashboard(
    projectId?: number,
  ): Promise<ManagementDashboard> {
    const qs = projectId === undefined ? '' : `?projectId=${projectId}`;
    return this.request<ManagementDashboard>(`/dashboards/management${qs}`);
  }
}
